import { queryOptions, useQuery } from '@tanstack/react-query';

import { useCartContext } from '@/app/contexts/CartProvider';
import useCurrency from '@/app/hooks/useCurrency';
import { graphql, type ResultOf, type VariablesOf } from '@/graphql';
import { getCartIdCookie } from '@/utils/cartUtils';
import gqlClient from '@/utils/gqlClient';

export const query = graphql(`
  query LowestRingsPricing(
    $cartId: String
    $currency: String
    $flags: [String]
    $includeDiscount: Boolean
    $region: String
    $ringStyle: String
  ) {
    content(region: $region, currency: $currency, enabledFeatureFlags: $flags) {
      lowestRingsPricing(
        cartId: $cartId
        includeDiscount: $includeDiscount
        ringStyle: $ringStyle
      ) {
        price {
          amount
          currencyCode
        }
      }
    }
  }
`);

export type LowestRingsPricingResult = ResultOf<typeof query>;
export type LowestRingsPricingVariables = VariablesOf<typeof query>;

const DEFAULT_VARS: LowestRingsPricingVariables = {
  cartId: null,
  currency: null,
  flags: [],
  includeDiscount: false,
  region: null,
  ringStyle: null,
};

export const lowestRingsPricingOptions = (
  vars: LowestRingsPricingVariables,
  enabled: boolean,
) => {
  const _vars: LowestRingsPricingVariables = {
    ...DEFAULT_VARS,
    ...vars,
  };

  // Automatically include cartId if includeDiscount is true
  if (_vars.includeDiscount && !_vars.cartId) {
    _vars.cartId = getCartIdCookie();
  }

  return queryOptions({
    queryKey: ['LowestRingsPricing', { _vars }],
    queryFn: async () => {
      const data = await gqlClient().request(query, _vars);
      return data.content?.lowestRingsPricing ?? { price: null };
    },
    enabled,
  });
};

export function useLowestRingsPricing(
  vars: Omit<LowestRingsPricingVariables, 'currency'> = {},
) {
  const currency = useCurrency();
  const { refreshCartSuccess } = useCartContext();

  return useQuery(
    lowestRingsPricingOptions(
      {
        ...vars,
        currency: currency.currencyCode,
      },
      refreshCartSuccess,
    ),
  );
}
